import React, { useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { property } from 'lodash';
import { Fab } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTitle } from '@dyl/systems/TitleManager';
import LinkButton from "@dyl/components/LinkButton";

import useAsync from '@dyl/hooks/useAsync';
import withLevel from "@dyl/utils/withLevel";
import TaskTypes from '@dyl/task-types';
import Skills from '@dyl/constants/Skills';
import DataGridDyl from '@dyl/components/DataGridDyl';
import { LevelText } from '@dyl/systems/LevelsManager';
import { getRubrics } from '@dyl/apiClients/backendApi';
import ScreenActions from "@dyl/components/ScreenActions";
import NewRubricModal from "@dyl/pages/Rubrics/list/NewRubricModal";
import GenericDeleteAction from "@dyl/components/GenericDeleteAction";
import deleteRubric from "@dyl/apiClients/backendApi/deleteRubric";


const getRowId = property('rubric_id');

export default withLevel(RubricList);

function RubricList({ globalLevel }) {
    useTitle('Rubrics');
    const { enqueueSnackbar } = useSnackbar();

    const getRowUrl = ({ row: { task_type_id, rubric_id }}) => (
        `/${globalLevel.label}/rubrics/${task_type_id}/${rubric_id}`
    );

    const [isLoading, setLoading] = useState(true);
    const [isNewMenuOpen, setNewMenuOpen] = useState(false);
    const [updateRubrics, setUpdateRubrics] = useState(0);

    const rubrics = useAsync(async () => {
        try {
            return await getRubrics(globalLevel.level_id);
        } catch {
            enqueueSnackbar('Failed to load rubrics', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [globalLevel, updateRubrics]) ?? [];

    const [sortModel, setSortModel] = useState(
        /** @returns {import('@mui/x-data-grid').GridSortModel} */
        () => [
            { field: 'task_type_id', sort: 'asc' },
        ]
    );

    /** @type {import('@mui/x-data-grid').GridColDef[]} */
    const Columns = useMemo(() => [
        {
            headerName: 'Level', field: 'level_id', flex: 1,
            renderCell({ value }) {
                return <LevelText levelId={value}/>;
            },
        },
        {
            headerName: 'Skill', field: 'skill_id', flex: 1,
            valueFormatter({ value }) {
                return Skills.find(s => s.value === value)?.label ?? 'Unknown';
            },
        },
        {
            headerName: 'Task Type', field: 'task_type_id', flex: 1,
            valueFormatter({ value }) {
                return TaskTypes[globalLevel.label].find(t => t.id === value)?.label ?? 'Unknown';
            }
        },
        // },
        { headerName: 'Existing Rubrics', field: 'rubric_text', flex: 1 },
        {
            headerName: 'Actions', field: 'ADMIN_DELETE', flex: 1,
            renderCell({ row }) {
                return (<GenericDeleteAction
                    data={row}
                    promptTexts={{
                        dialogTitle: () => 'Delete Rubric?',
                        dialogText: (data) => [
                            `Are you sure you want to delete rubric "${data.rubric_text}"?`
                        ],
                        ongoingNotification: (data) => `Deleting "${data.rubric_text}"...`,
                        successNotification: (data, response) => `Deleted "${data.rubric_text}".`,
                        failureNotification: (data, response) => `The rubric you are trying to delete is associated with the following task(s): `
                            + `${response.associated_tasks.map(x => x.task_id_name).join(', ')},`
                            + `please remove the rubric from the associated task(s) in order to delete it.`,
                        errorNotification: (data) => `Failed to delete "${data.rubric_text}".`
                    }
                    }
                    deleteAction={(data) => deleteRubric(data)}
                    onDelete={() => setUpdateRubrics(updateRubrics + 1)}
                    responseSuccessPredicate={(response) => response.success}
                />)
            }
        },
    ], [globalLevel]);

    return (
        <>
            <DataGridDyl
                columns={Columns}
                rows={rubrics}
                loading={isLoading}
                getRowId={getRowId}
                variant='links'
                getRowUrl={getRowUrl}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
            />

            <ScreenActions>
                <LinkButton
                    component={Fab}
                    color='primary'
                    variant='extended'
                    size='large'
                    onClick={() => setNewMenuOpen(true)}
                >
                    <AddIcon/>&nbsp;New
                </LinkButton>
            </ScreenActions>

            <NewRubricModal open={isNewMenuOpen} onClose={() => setNewMenuOpen(false)}/>
        </>
    );
}